import { useState, useEffect } from 'react';

/**
 * GlobalOutputDrawer — Edit campus-level global output settings.
 *
 * Props:
 *   open            — drawer visibility
 *   onClose         — close callback
 *   onSave          — save callback (receives formData, oldPayload)
 *   saving          — boolean, disables buttons while saving
 *   campusSettings  — object with current campus output flags
 *   campusId        — current campus ID
 */
const GlobalOutputDrawer = ({ open, onClose, onSave, saving, campusSettings, campusId }) => {
    const [mobile, setMobile] = useState(false);
    const [text, setText] = useState(false);
    const [voice, setVoice] = useState(false);
    const [email, setEmail] = useState(false);
    const [pager, setPager] = useState(false);
    const [serial, setSerial] = useState(false);
    const [offsite, setOffsite] = useState(false);

    // Reset form when drawer opens or campusSettings changes
    useEffect(() => {
        if (open && campusSettings) {
            setMobile(campusSettings.app_active === 1);
            setText(campusSettings.text_messaging_active === 1);
            setVoice(campusSettings.voice_active === 1);
            setEmail(campusSettings.email_active === 1);
            setPager(campusSettings.pager_active === 1);
            setSerial(campusSettings.serial_active === 1);
            setOffsite(campusSettings.offsite_active === 1);
        }
    }, [open, campusSettings]);

    const handleSave = () => {
        const formData = {
            app_active: mobile ? 1 : 0,
            text_messaging_active: text ? 1 : 0,
            voice_active: voice ? 1 : 0,
            email_active: email ? 1 : 0,
            pager_active: pager ? 1 : 0,
            serial_active: serial ? 1 : 0,
            offsite_active: offsite ? 1 : 0,
            campus_id: campusId,
        };

        const oldPayload = {
            app_active: campusSettings?.app_active ?? 0,
            text_messaging_active: campusSettings?.text_messaging_active ?? 0,
            voice_active: campusSettings?.voice_active ?? 0,
            email_active: campusSettings?.email_active ?? 0,
            pager_active: campusSettings?.pager_active ?? 0,
            serial_active: campusSettings?.serial_active ?? 0,
            offsite_active: campusSettings?.offsite_active ?? 0,
            campus_id: campusId,
        };

        onSave(formData, oldPayload);
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Global Output Settings</h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {/* Warning banner */}
                    <div className="dp-drawer__field" style={{ color: 'var(--color-danger, #c53030)' }}>
                        <i className="fe fe-alert-triangle" style={{ marginRight: 6 }} />
                        Warning: Changing output settings will reboot the local server
                    </div>

                    {/* PalCare Mobile */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">PalCare Mobile</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={mobile}
                                onChange={(e) => setMobile(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Text Messaging */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Text Messaging</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={text}
                                onChange={(e) => setText(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Voice Calls */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Voice Calls</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={voice}
                                onChange={(e) => setVoice(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Emails */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Emails</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={email}
                                onChange={(e) => setEmail(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Pagers */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Pagers</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={pager}
                                onChange={(e) => setPager(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Serial Out */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Serial Out</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={serial}
                                onChange={(e) => setSerial(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>

                    {/* Offsite Monitoring */}
                    <div className="ws-toggle-row">
                        <span className="ws-toggle-label">Offsite Monitoring</span>
                        <label className="dp-toggle">
                            <input
                                type="checkbox"
                                checked={offsite}
                                onChange={(e) => setOffsite(e.target.checked)}
                            />
                            <span className="dp-toggle__track" />
                        </label>
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default GlobalOutputDrawer;
